import { action, makeObservable, observable, runInAction } from "mobx";
import { randmarApiWithTags } from "randmar-api-client";
import { store } from "../../new-store/store";
import Manufacturer, { InventoryValue } from "./Manufacturer";

class ManufacturerStore {
  manufacturer?: Manufacturer | null = undefined;
  inventoryValue?: InventoryValue[] = undefined;

  constructor() {
    makeObservable(this, {
      manufacturer: observable,
      inventoryValue: observable,
      getManufacturerInfo: action.bound,
      updateManufacturerProperty: action.bound,
      getInventoryValue: action.bound,
      downloadReport: action.bound,
      downloadSales: action.bound,
      downloadInventory: action.bound,
      downloadClaim: action.bound,
      sendManufacturerFeedback: action.bound
    });
  }

  async getManufacturerInfo(applicationId: string) {
    try {
      const info = await store.dispatch(
        randmarApiWithTags.endpoints.getV4ManufacturerByRouteManufacturerIdAccount.initiate({
          routeManufacturerId: applicationId,
          withSpecification: true,
        })
      ).unwrap();
      const manufacturer = Manufacturer.convertFrom(info);

      runInAction(() => {
        this.manufacturer = manufacturer;
        document.title = (manufacturer.publicName || manufacturer.name) + " | Randmar Inc.";
      });
    } catch (e) {
      console.error(e);
      runInAction(() => {
        this.manufacturer = null;
      });
    }
  }

  async updateManufacturerProperty(applicationId: string, fieldId: string, newValue: string): Promise<boolean> {
    try {
      await store.dispatch(
        randmarApiWithTags.endpoints.postV4PartnerByApplicationIdAccountManufacturerAndFieldName.initiate({
          applicationId,
          fieldName: fieldId,
          newValue,
        })
      ).unwrap();
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async getInventoryValue(applicationId: string) {
    try {
      const res = await store.dispatch(
        randmarApiWithTags.endpoints.getV4ManufacturerByRouteManufacturerIdInventoryValue.initiate({
          routeManufacturerId: applicationId,
        })
      ).unwrap();

      const inventory = res.map(InventoryValue.convertFrom);
      runInAction(() => {
        this.inventoryValue = inventory;
      });
    } catch (e) {
      console.error(e);
    }
  }

  async downloadReport(applicationId: string, type: string, email: string): Promise<boolean> {
    try {
      const routeManufacturerId = applicationId;
      const emailAddress = email;

      switch (type) {
        case "OpenOrders":
          await store.dispatch(
            randmarApiWithTags.endpoints.postV4ManufacturerByRouteManufacturerIdReportOpenOrdersEmail.initiate({
              routeManufacturerId,
              emailAddress,
            })
          ).unwrap();
          return true;
        case "Products":
          await store.dispatch(
            randmarApiWithTags.endpoints.postV4ManufacturerByRouteManufacturerIdReportProductsEmail.initiate({
              routeManufacturerId,
              emailAddress,
            })
          ).unwrap();
          return true;
        case "PurchasedOrders":
          await store.dispatch(
            randmarApiWithTags.endpoints.postV4ManufacturerByRouteManufacturerIdReportPurchasedOrdersEmail.initiate({
              routeManufacturerId,
              emailAddress,
            })
          ).unwrap();
          return true;
        case "Reorder":
          await store.dispatch(
            randmarApiWithTags.endpoints.postV4ManufacturerByRouteManufacturerIdReportReorderEmail.initiate({
              routeManufacturerId,
              emailAddress,
            })
          ).unwrap();
          return true;
        default:
          console.error(`Unsupported report type: ${type}`);
          return false;
      }
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async downloadSales(applicationId: string, email: string, dateStart: string, dateEnd: string, resellerId?: string): Promise<boolean> {
    try {
      const routeManufacturerId = applicationId;
      const emailAddress = email;

      if (resellerId) {
        await store.dispatch(
          randmarApiWithTags.endpoints.postV4ManufacturerByRouteManufacturerIdReportSalesAndResellerIdEmail.initiate({
            routeManufacturerId,
            resellerId,
            emailAddress,
            startDate: dateStart,
            endDate: dateEnd,
          })
        ).unwrap();
        return true;
      }

      await store.dispatch(
        randmarApiWithTags.endpoints.postV4ManufacturerByRouteManufacturerIdReportSalesEmail.initiate({
          routeManufacturerId,
          emailAddress,
          startDate: dateStart,
          endDate: dateEnd,
        })
      ).unwrap();
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async downloadClaim(applicationId: string, email: string, dateStart: string, dateEnd: string): Promise<boolean> {
    try {
      await store.dispatch(
        randmarApiWithTags.endpoints.postV4ManufacturerByRouteManufacturerIdReportClaimEmail.initiate({
          routeManufacturerId: applicationId,
          emailAddress: email,
          startDate: dateStart,
          endDate: dateEnd,
        })
      ).unwrap();
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async downloadInventory(applicationId: string, email: string, inventoryDate: string): Promise<boolean> {
    try {
      await store.dispatch(
        randmarApiWithTags.endpoints.postV4ManufacturerByRouteManufacturerIdReportInventoryEmail.initiate({
          routeManufacturerId: applicationId,
          emailAddress: email,
          inventoryDate,
        })
      ).unwrap();
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async sendManufacturerFeedback(applicationId: string, emailAddress: string, comment: string): Promise<boolean> {
    try {
      await store.dispatch(
        randmarApiWithTags.endpoints.postV4PartnerByApplicationIdAccountFeedback.initiate({
          applicationId,
          emailAddress,
          comment,
        })
      ).unwrap();
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }
}

export default ManufacturerStore;
